import { defineStore } from 'pinia';
import {useOrdersStore} from "@/console-new/stores/orders.store";
import {useUserStore} from "@/console-new/stores/user.store";
import API from "@/shared/helpers/API";
import {replaceNonAlphanumeric} from "@/shared/helpers/strings";
import {Epson} from "@/console-new/helpers/epson";
import {orderFiscalizedStatus} from "@/console-new/helpers/orders";
import {useMenuStore, useNativeStore, useRoomsStore, useFunnelStore} from "@/console-new/stores";
import {useCurrencyStore} from "@/shared/stores";
import {getNative, isNative} from "@/shared/helpers/webapp";
import {useNotificationStore} from "@/shared/stores";
import {getLocalDateFromServer} from '@/shared/helpers/dates';

export const useEpsonStore = defineStore('epson', {
	state: () => ({
		fiscalPrinterFailedSilent: false,
		fiscalPrinterFailed: false,
		printReportResult: null,
		countryVats: {
			IT: ["10%", "22%", "4%", "5%", "0%"],
			ES: ["10%", "21%", "4%"],
			FR: ["10%", "5.5%", "20%", "2.1%"],
			DE: ["7%", "19%"],
		},
		countryVatsNature: {
			IT: {
				"N1": ["Escluso dalla base imponibile art.15"],
				"N2.1": [
					"Art.7 bis DPR 633/1972 (cessione a UE e extra-UE di beni non esistenti fisicamente in Italia)",
					"Art.7 ter  DPR 633/1972 prestazione servizi a UE (vendite)",
					"Art.7 ter  DPR 633/1972 prestazione servizi a extra-UE",
					"Art.7 quater DPR 633/1972 prestazione servizi particolari a UE e extra-UE",
					"Art.7 quinquies DPR 633/1972 prestazione servizi particolari a UE e extra-UE",
					"Art.7 sexies DPR 633/1972 prestazione servizi particolari a UE e extra-UE",
					"Art.7 septies DPR 633/1972 prestazione servizi particolari a UE e extra-UE"
				],
				"N2.2": [
					"Fuori campo Iva art. 2, 3, 4, 5 DPR 633/1972",
					"Art. 38 c.5 DL  331/1993",
					"Art.17 c.3 DPR 633/1972",
					"Art.19 c.3 lett. b DPR 633/1972",
					"Art. 50 bis c.4 DL 331/1993",
					"Art.74 cc.1 e 2 DPR 633/1972",
					"Art.19 c.3 lett. e DPR 633/1972",
					"Art.13 DPR 633/1972",
					"Art. 27 c.1 e 2 DL 98/2011 (contribuenti minimi)",
					"Art.1 c.54-89 L. 190/2014 e succ. modifiche (regime forfettario)",
					"Art.26 c.3 DPR 633/1972",
					"DM 9/4/1993",
					"Art.26 bis L.196/1997",
					"Art.8 c.35 L. 67/1988",
					"Non soggette - altri casi"
				],
				"N3.1": [
					"Art.8 c.1 lett.a DPR 633/1972 (cessione beni a extra-UE, trasporto a cure del cedente)",
					"Art.8 c.1 lett.b DPR 633/1972  (cessione beni a extra-UE, trasporto a cure del cessionario)",
					"Non imp. art. 8 c.1 lett. b-bis  DPR 633/1972",
					"Non imp. art. 8 bis c.2 DPR 633/1972",
					"Non imp. art.58 c.1 DL 331/93",
					"Non imp. art.38 quater c.1 DPR 633/1972"
				],
				"N3.2": ["Non imp. art.41 DL 331/93 (cessione beni a UE)"],
				"N3.3": ["Art. 71 DPR 633/1972"],
				"N3.4": ["Art. 8 bis DPR 633/1972"],
				"N3.5": ["Non imp. art. 8 c.1 lett. c DPR 633/1972"],
				"N3.6": [
					"Art. 2 c. 2, n. 4 DPR 633/1972",
					"Art.9 c.1 DPR 633/1972",
					"Non imp. art.72 DPR 633/1972",
					"Non imp. art.9 c.2 DPR 633/1972",
					"Non imp. art.50 bis c.4 lett. f g DL 331/93",
					"Non imp. art.50 bis c.4 lett. g DL 331/93",
					"Non imp. art.50 bis c.4 lett. f DL 331/93",
					"Art.74 ter c.8 DPR 633/1972",
					"Non imp. art.14 legge n. 49/1987"
				],
				"N4": [
					"Esente art.10 DPR 633/1972",
					"Esente a.19 c.3 lett.a bis DPR 633/1972",
					"Esente Art. 124 co. 2 DL 34/2020 (mascherine)"
				],
				"N5": [
					"Art.36 DL n.41/1995",
					"Art. 74 ter DPR 633/1972 (Regime speciale agenzie di viaggio)"
				]
			},
		},
		natureToDept: {
			"N1": 10,
			"N2.1": 11,
			"N2.2": 11,
			"N3.1":	12,
			"N3.2": 13,
			"N3.3": 13,
			"N3.4": 13,
			"N3.5": 13,
			"N3.6": 13,
			"N4": 14,
			"N5": 15,
		},
		zreportToReinjest: null,
		reinjestStatus: null,
		businessEpsonZReports: null,
		businessEpsonZReportsTotal: null,
	}),
	getters: {
		hasFiscalPrinter: state => {
			const nativeStore = useNativeStore();
			return isNative() && nativeStore.fiscalIp !== "";
		}
	},
	actions: {
		formatPriceString(price) {
			return useCurrencyStore().dbToActualValue(price).toFixed(2).replace(".",",");
		},
		generateFiscalReceiptApiscontrino(orders, callback) {

			const userStore = useUserStore();
			API.init().generateFiscalReceiptApiscontrino(userStore.currBusinessId, orders, (r) => {
				if(r.result && r.result === "OK" && r.receipt_id) {
					callback && callback(r.receipt_id);
				} else {
					callback && callback(false);
				}
			});

		},
		logError(rawRequest, rawResponse, code, lastCommand, printerStatus) {

			const userStore = useUserStore();
			API.init().logError(userStore.currBusinessId, rawRequest, rawResponse, code, lastCommand, printerStatus, () => {

			});
		},
		printFiscalReceipt(orders, retrying = false) {

			if(!isNative()) {
				return;
			}

			const nativeStore = useNativeStore();
			let excludedStations = nativeStore.excludedStations.map(s => s !== "null" ? parseInt(s) : s);

			const userStore = useUserStore();
			let orderIds = orders.map((o) => o.order_id);
			API.init().checkOrdersFiscal(userStore.currBusinessId, orderIds, (r) => {
				if(r.result && r.result === "OK" && r.orders) {

					let orderIdsFound = [];
					const orderMap = {};
					orders.forEach((order) => {
						orderMap[order.order_id+""] = order;
					});
					r.orders.forEach((order) => {
						let orderId = order.order_id;
						let orderFound = orderMap.hasOwnProperty(orderId+"") ? orderMap[orderId+""] : null;
						if(orderFound && order.hasOwnProperty("full_by_single_payment")) {
							orderFound.full_by_single_payment = order.full_by_single_payment;
						}

						if((retrying || !order.fiscal_request) && !order.fiscal_receipt) {
							//let orderFound = orders.find((o) => o.order_id === orderId);
							if(orderFound) {
								if(orderFound.type === "manual") {
									return;
								}

								if(order.type === "multi") {
									return;
								}

								if(orderFound.paid) {
									if(excludedStations.includes(order.payment_user_id ? order.payment_user_id : "null")) {
										return;
									}
									orderIdsFound.push(orderId);
									if(order.room_table_id && orderFound?.room?.cover_charge_name) {
										this.generateFiscalReceiptForOrder(orderFound, retrying, orderFound.room.cover_charge_name);
									} else {
										this.generateFiscalReceiptForOrder(orderFound, retrying);
									}
								}

							} else {
								console.warn("Order not found for fiscal receipt: "+order.order_id);
							}
						}
					});

					if(r.payments && r.payments.length > 0) {
						r.payments.forEach((payment) => {
							if(payment.type === "manual") {
								return;
							}
							if(excludedStations.includes(payment.payment_user_id ? payment.payment_user_id : "null")) {
								return;
							}
							if((retrying || !payment.fiscal_request) && !payment.fiscal_receipt) {
								let ordersFound = [];
								let coverChargeName = null;
								payment.order_ids.forEach((orderId) => {
									let orderFound = orderMap.hasOwnProperty(orderId) ? orderMap[orderId] : null;
									if(orderFound) {
										ordersFound.push(orderFound);
										if(orderFound.room_table_id && !coverChargeName && orderFound?.room?.cover_charge_name) {
											coverChargeName = orderFound.room.cover_charge_name;
										}
									}
								});
								orderIdsFound.push("p"+payment.order_payment_id);
								this.generateFiscalReceiptForPayment(ordersFound, payment, retrying, coverChargeName);
							}
						});
					}

					if(orderIdsFound.length === 0) {
						console.warn("No orders to print fiscal receipt")
						this.fiscalPrinterFailedSilent = true;
					}

				} else {
					console.warn("No orders to print fiscal receipt")
					this.fiscalPrinterFailedSilent = true;
				}
			});

		},
		vatToDepartment(vat, nature = '') {
			let vatInt = parseInt(vat);
			if(vatInt === 10) {
				return 1;
			} else if(vatInt === 4) {
				return 2;
			} else if(vatInt === 22) {
				return 3;
			} else if(vatInt === 5) {
				return 4;
			} else if (vatInt === 0){
				let natDept = this.natureToDept['N2.2'];
				if (this.natureToDept.hasOwnProperty(nature)) {
					natDept = this.natureToDept[nature];
				}
				return natDept;
			} else {
				return 1;
			}
		},
		generateFiscalReceiptForOrder(orderFound, retrying, coverChargeName = null) {
			let email = orderFound.user_email;
			let total = 0;
			let data = "";
			let paymentDesc = "Contanti";
			let paymentMode = "0";

			const menuStore = useMenuStore();
			const userStore = useUserStore();
			
			// Check if we should use consolidated receipt
			if (orderFound.type !== "multi" && !!orderFound.print_single_item) {
				// Consolidate items by VAT rate for thermal printing
				let vatMap = {};
				
				orderFound.items.forEach(item => {
					let department = 1;
					let country = userStore.currBusiness.country ?? 'IT';
					let nature = this.parseVatNature(country, item.menu_item?.natura_iva ?? 'N2.2.0').nature;
					if(item.menu_item && item.menu_item.vat !== null) {
						department = this.vatToDepartment(item.menu_item.vat, nature);
					} else if(item.category_id) {
						let category = menuStore.categories[item.category_id];
						if(category && category.vat !== null) {
							department = this.vatToDepartment(category.vat, nature);
						}
					}
					
					let itemTotal = item.price * item.quantity;
					total += itemTotal;
					
					// Add product costs to item total
					item.products.forEach(product => {
						let productTotal = product.price * product.quantity * item.quantity;
						itemTotal += productTotal;
						total += productTotal;
					});
					
					if (!vatMap[department]) {
						vatMap[department] = {
							price: 0,
							department: department
						};
					}
					vatMap[department].price += itemTotal;
				});
				
				// Generate consolidated receipt items
				for (let department in vatMap) {
					let consolidatedPrice = this.formatPriceString(vatMap[department].price);
					let totalTitle = userStore.currBusiness.counter_total_title || "Pasto completo";
					let consolidatedDesc = replaceNonAlphanumeric(totalTitle).slice(0, 35);
					data += '<printRecItem operator="1" description="'+consolidatedDesc+'" quantity="1" unitPrice="'+consolidatedPrice+'" department="'+department+'" justification="1" />';
				}
			} else {
				// Use normal detailed receipt
				orderFound.items.forEach(item => {
					let quantity = item.quantity;
					let price = this.formatPriceString(item.price);
					total += item.price * item.quantity;
					let desc = replaceNonAlphanumeric(item.name).slice(0, 35);
					let department = 1;
					let country = userStore.currBusiness.country ?? 'IT';
					let nature = this.parseVatNature(country, item.menu_item?.natura_iva ?? 'N2.2.0').nature;
					if(item.menu_item && item.menu_item.vat !== null) {
						department = this.vatToDepartment(item.menu_item.vat, nature);
					} else if(item.category_id) {
						let category = menuStore.categories[item.category_id];
						if(category && category.vat !== null) {
							department = this.vatToDepartment(category.vat, nature);
						}
					}
					data += '<printRecItem operator="1" description="'+desc+'" quantity="'+quantity+'" unitPrice="'+price+'" department="'+department+'" justification="1" />';
					item.products.forEach(product => {
						let quantityProd = product.quantity * item.quantity;
						let priceProd = this.formatPriceString(product.price);
						total += product.price * quantityProd;
						let descProd = replaceNonAlphanumeric(product.product_name).slice(0, 35);
						data += '<printRecItem operator="1" description="'+descProd+'" quantity="'+quantityProd+'" unitPrice="'+priceProd+'" department="'+department+'" justification="1" />';
					});
				});
			}

			let additionalCosts = orderFound.additionalCosts - (orderFound.subtotal_delta + orderFound.tip);
			if(additionalCosts > 0) {
				let nameAddCost;
				if(orderFound.order_mode === "delivery") {
					nameAddCost = "Costi di consegna";
				} else if(orderFound.order_mode === "takeaway") {
					nameAddCost = "Costi aggiuntivi";
				} else {
					nameAddCost = coverChargeName ? replaceNonAlphanumeric(coverChargeName) : "Coperto";
				}
				data += '<printRecItem operator="1" description="'+nameAddCost+'" quantity="1" unitPrice="'+this.formatPriceString(additionalCosts)+'" department="1" justification="1" />';
			}

			if(orderFound.tip > 0) {
				let formattedTip = this.formatPriceString(orderFound.tip);
				data += '<printRecItem operator="1" description="Mancia" quantity="1" unitPrice="'+formattedTip+'" department="11" justification="1" />';
			}

			if(orderFound.items.length === 0 && orderFound.subtotal_delta > 0) {
				data += '<printRecItem operator="1" description="Importo" quantity="1" unitPrice="'+this.formatPriceString(orderFound.subtotal_delta)+'" department="1" justification="1" />';
			} else if(orderFound.subtotal_delta !== 0) {
				let discountName = orderFound.subtotal_delta > 0 ? "Maggiorazione" : "Sconto";
				let discountType = orderFound.subtotal_delta > 0 ? "6" : "1";
				let discountAmount = orderFound.subtotal_delta > 0 ? orderFound.subtotal_delta : -orderFound.subtotal_delta;
				let discountFormatted = this.formatPriceString(discountAmount);
				data += '<printRecSubtotalAdjustment Ope="1" Text="'+discountName+'" Type="'+discountType+'" Amount="'+discountFormatted+'" Dep="0" Just="2" />';
			}

			let paymentInfo = this.getEpsonPaymentInfoFromType(orderFound.type);
			paymentDesc = paymentInfo.paymentDesc;
			paymentMode = paymentInfo.paymentMode;

			if(orderFound.type === "multi") {
				if(orderFound.payments) {

					let grandTotal = total+additionalCosts+orderFound.subtotal_delta+orderFound.tip;
					for(let i = 0; i < orderFound.payments.length; i++) {
						if(orderFound.payments[i].amount <= 0) {
							continue;
						}
						paymentInfo = this.getEpsonPaymentInfoFromType(orderFound.payments[i].type);
						paymentDesc = paymentInfo.paymentDesc;
						paymentMode = paymentInfo.paymentMode;
						total = this.formatPriceString(orderFound.payments[i].amount);
						grandTotal -= orderFound.payments[i].amount;
						data += '<printRecTotal operator="1" description="'+paymentDesc+'" payment="'+total+'" paymentType="'+paymentMode+'" index="3" justification="1" />';
					}
					if(grandTotal > 0) {
						paymentDesc = "Pagamenti multipli";
						total = this.formatPriceString(grandTotal);
						data += '<printRecTotal operator="1" description="'+paymentDesc+'" payment="'+total+'" paymentType="'+paymentMode+'" index="3" justification="1" />';
					}

				} else {
					paymentDesc = "Pagamenti multipli";
					total = this.formatPriceString(total+additionalCosts+orderFound.subtotal_delta+orderFound.tip);
					data += '<printRecTotal operator="1" description="'+paymentDesc+'" payment="'+total+'" paymentType="'+paymentMode+'" index="3" justification="1" />';
				}
			} else {
				total = this.formatPriceString(total+additionalCosts+orderFound.subtotal_delta+orderFound.tip);
				data += '<printRecTotal operator="1" description="'+paymentDesc+'" payment="'+total+'" paymentType="'+paymentMode+'" index="3" justification="1" />';
			}

			if(orderFound.orderNumberDayFormatted) {
				data += '<printRecMessage  operator="1" messageType="3" index="1" font="4" message="Ordine '+orderFound.orderNumberDayFormatted+'" />';
			}
			if(orderFound.table_number) {
				data += '<printRecMessage  operator="1" messageType="3" index="2" font="4" message="-> '+replaceNonAlphanumeric(orderFound.table_number)+'" />';
			}

			const nativeStore = useNativeStore();
			if(nativeStore.isQrFiscalReceipt && orderFound.id_encrypted && userStore.currBusiness.link) {
				let link = (LOCAL ? "http://" : "https://") + userStore.currBusiness.link + "." + HOST + "/order/?id="+orderFound.id_encrypted;
				data += '<printBarCode operator="1" qRCodeAlignment="1" qRCodeSize ="7" qRCodeErrorCorrection="1" codeType="QRCODE2" code="'+link+'" />';
			}

			Epson.addToQueue({
				ids: JSON.stringify([Number(orderFound.order_id)]),
				data: data,
				email: email
			}, retrying);
		},
		generateFiscalReceiptForPayment(ordersFound, payment, retrying, coverChargeName = null) {
			if(ordersFound.length === 0) {
				return;
			}
			let email = ordersFound[0].user_email;
			let data = "";
			let paymentDesc = "Contanti";
			let paymentMode = "0";

			let departmentsMap = {};
			let explicitItemsTotal = 0;
			let explicitAddCosts = 0;
			let explicitCover = 0;

			const menuStore = useMenuStore();
			const userStore = useUserStore();
			ordersFound.forEach(order => {
				const fullBySinglePayment = order.hasOwnProperty("full_by_single_payment") && order.full_by_single_payment;

				if(fullBySinglePayment) {
					explicitAddCosts += order.delivery_costs_value + order.takeaway_costs_value + order.subtotal_delta + order.tip;
					explicitCover += order.cover_charge_value*order.cover_charge_num;
				}

				let country = userStore.currBusiness.country ?? 'IT';
				order.items.forEach(item => {
					let department = 1;
					let nature = this.parseVatNature(country, item.menu_item?.natura_iva ?? 'N2.2.0').nature;
					if(item.menu_item && item.menu_item.vat) {
						department = this.vatToDepartment(item.menu_item.vat, nature);
					} else if(item.category_id) {
						let category = menuStore.categories[item.category_id];
						if(category && category.vat) {
							department = this.vatToDepartment(category.vat, nature);
						}
					}

					if(fullBySinglePayment) {

						let quantity = item.quantity;
						let price = this.formatPriceString(item.price);
						let desc = replaceNonAlphanumeric(item.name).slice(0, 35);

						explicitItemsTotal += item.price * item.quantity;
						data += '<printRecItem operator="1" description="'+desc+'" quantity="'+quantity+'" unitPrice="'+price+'" department="'+department+'" justification="1" />';
						item.products.forEach(product => {
							let quantityProd = product.quantity * item.quantity;
							let priceProd = this.formatPriceString(product.price);
							let descProd = replaceNonAlphanumeric(product.product_name).slice(0, 35);
							data += '<printRecItem operator="1" description="'+descProd+'" quantity="'+quantityProd+'" unitPrice="'+priceProd+'" department="'+department+'" justification="1" />';

							explicitItemsTotal += product.price * quantityProd;
						});

					} else {

						if(!departmentsMap[department]) {
							departmentsMap[department] = 0;
						}
						departmentsMap[department] += item.price * item.quantity;
						item.products.forEach(product => {
							departmentsMap[department] += (product.price * product.quantity) * item.quantity;
						});

					}
				});

			});

			// Check if we should use consolidated receipt for payment
			const totalTitle = userStore.currBusiness.counter_total_title || "Pasto completo";
			const hasMultiOrder = ordersFound.some(order => order.type === "multi");
			const hasPrintSingleItem = ordersFound.some(order => order.print_single_item);

			// Only use consolidation if NO orders are multi AND at least one has print_single_item
			let splitTitle = "Quota";
			if (!hasMultiOrder && hasPrintSingleItem) {
				splitTitle = replaceNonAlphanumeric(totalTitle);
			} else if(userStore.currBusiness.counter_split_title) {
				splitTitle = replaceNonAlphanumeric(userStore.currBusiness.counter_split_title);
			}
			if(payment.dutch_people_paid && payment.dutch_people_paid > 1) {
				splitTitle += " (x"+payment.dutch_people_paid+" Persone)";
			}
			let amount = Number(payment.amount);
			if(amount <= 0) {
				return;
			}
			let departmentsTotal = Object.values(departmentsMap).reduce((a, b) => a + b, 0);
			let remainingAmount = amount;
			if(departmentsTotal > 0) {
				for(let department in departmentsMap) {
					let price = Math.floor((departmentsMap[department]/departmentsTotal) * amount);
					if(price > 0) {
						remainingAmount -= price;
						price = this.formatPriceString(price);
						data += '<printRecItem operator="1" description="'+splitTitle+'" quantity="1" unitPrice="'+price+'" department="'+department+'" justification="1" />';
					}
				}
			}
			remainingAmount -= explicitItemsTotal;
			remainingAmount -= explicitAddCosts;
			remainingAmount -= explicitCover;
			if(remainingAmount >= 1) {
				let department = 1;
				let price = this.formatPriceString(remainingAmount);
				data += '<printRecItem operator="1" description="'+splitTitle+'" quantity="1" unitPrice="'+price+'" department="'+department+'" justification="1" />';
			}

			let paymentInfo = this.getEpsonPaymentInfoFromType(payment.type);
			paymentDesc = paymentInfo.paymentDesc;
			paymentMode = paymentInfo.paymentMode;

			if(explicitCover > 0) {
				let explicitCoverName = coverChargeName ? replaceNonAlphanumeric(coverChargeName) : "Coperto";
				explicitCoverName = explicitCoverName || "Coperto";
				data += '<printRecItem operator="1" description="'+explicitCoverName+'" quantity="1" unitPrice="'+this.formatPriceString(explicitCover)+'" department="1" justification="1" />';
			}

			if(explicitAddCosts !== 0) {
				let discountName = explicitAddCosts > 0 ? "Maggiorazione" : "Sconto";
				let discountType = explicitAddCosts > 0 ? "6" : "1";
				let discountAmount = explicitAddCosts > 0 ? explicitAddCosts : -explicitAddCosts;
				let discountFormatted = this.formatPriceString(discountAmount);
				data += '<printRecSubtotalAdjustment Ope="1" Text="'+discountName+'" Type="'+discountType+'" Amount="'+discountFormatted+'" Dep="0" Just="2" />';
			}

			if(payment.tip && payment.tip > 0){
				let formattedTip = this.formatPriceString(payment.tip);
				data += '<printRecItem operator="1" description="Mancia" quantity="1" unitPrice="'+formattedTip+'" department="11" justification="1" />';
				amount += Number(payment.tip);
			}

			let amountString = this.formatPriceString(amount);
			data += '<printRecTotal operator="1" description="'+paymentDesc+'" payment="'+amountString+'" paymentType="'+paymentMode+'" index="3" justification="1" />';

			let orderPart = null;
			let tablePart = null;
			if(ordersFound.length > 1) {
				orderPart = "Ordini ";
				tablePart = "-> ";
				ordersFound.forEach((order, index) => {
					orderPart += order.orderNumberDayFormatted;
					if(index < ordersFound.length-1) {
						orderPart += ", ";
					}
					if(order.table_number) {
						tablePart += replaceNonAlphanumeric(order.table_number);
						if(index < ordersFound.length-1) {
							tablePart += ", ";
						}
					}
				});
			} else {
				if(ordersFound[0].orderNumberDayFormatted) {
					orderPart = "Ordine "+ordersFound[0].orderNumberDayFormatted;
					if(ordersFound[0].table_number) {
						tablePart = "-> "+replaceNonAlphanumeric(ordersFound[0].table_number);
					}
				}
			}
			data += '<printRecMessage  operator="1" messageType="3" index="1" font="4" message="'+orderPart+' - Pagamento #'+payment.payment_id+'" />';
			if(tablePart) {
				data += '<printRecMessage  operator="1" messageType="3" index="2" font="4" message="'+tablePart+'" />';
			}

			const nativeStore = useNativeStore();
			if(nativeStore.isQrFiscalReceipt && ordersFound[0].id_encrypted && userStore.currBusiness.link) {
				let link = (LOCAL ? "http://" : "https://") + userStore.currBusiness.link + "." + HOST + "/order/?id="+ordersFound[0].id_encrypted;
				data += '<printBarCode operator="1" qRCodeAlignment="1" qRCodeSize ="7" qRCodeErrorCorrection="1" codeType="QRCODE2" code="'+link+'" />';
			}

			Epson.addToQueue({
				ids: JSON.stringify(["p"+Number(payment.payment_id)]),
				data: data,
				email: email
			}, retrying);
		},
		customFiscalReceipt(description, amount, paymentType) {

			if(!isNative()) {
				return;
			}
			if(paymentType === "manual") {
				return;
			}

			let data = "";
			let paymentDesc = "Contanti";
			let paymentMode = "0";

			let amountString = this.formatPriceString(amount);
			data += '<printRecItem operator="1" description="'+description+'" quantity="1" unitPrice="'+amountString+'" department="1" justification="1" />';

			let paymentInfo = this.getEpsonPaymentInfoFromType(paymentType);
			paymentDesc = paymentInfo.paymentDesc;
			paymentMode = paymentInfo.paymentMode;


			data += '<printRecTotal operator="1" description="'+paymentDesc+'" payment="'+amountString+'" paymentType="'+paymentMode+'" index="3" justification="1" />';

			if(isNative()) {
				getNative().printFiscalReceipt("[]", data, null);
			}

		},
		getEpsonPaymentInfoFromType(type) {
			let paymentDesc = "Contanti";
			let paymentMode = "0";
			if(type === "stripe") {
				paymentDesc = "Stripe, carta";
				paymentMode = "2";
			} else if(type === "klarna") {
				paymentDesc = "Stripe, klarna";
				paymentMode = "2";
			} else if(type === "satispay") {
				paymentDesc = "Satispay";
				paymentMode = "2";
			} else if(type === "btc") {
				paymentDesc = "Bitcoin";
				paymentMode = "2";
			} else if(type === "ticket") {
				paymentDesc = "Ticket";
				paymentMode = "3";
			} else if(type === "paylater") {
				paymentDesc = "Non riscosso";
				paymentMode = "5";
			} else if(type === "check") {
				paymentDesc = "Assegno";
				paymentMode = "1";
			} else if(type === "wire") {
				paymentDesc = "Bonifico";
				paymentMode = "1";
			} else if(type === "external") {
				paymentDesc = "Pagamento esterno";
				paymentMode = "2";
			}
			return {
				paymentDesc,
				paymentMode
			}
		},
		fiscalReceiptSuccess(orderIds, receiptData) {
			const userStore = useUserStore();
			const ordersStore = useOrdersStore();
			API.init().setOrdersFiscal(userStore.currBusinessId, orderIds, receiptData, (r) => {
				if(r.result && r.result === "OK") {
					ordersStore.ordersUngrouped.forEach((order) => {
						orderIds.forEach((orderId) => {
							if(order.order_id === orderId) {
								order.fiscal_receipt = true;
							}
						});
						order.payments.forEach((payment) => {
							orderIds.forEach((orderId) => {
								if ("p" + payment.payment_id === orderId) {
									payment.fiscal_receipt = true;
								}
							});
						});
					});
				}
			});
		},
		setOrdersFiscalSent(orderIds, retrying, callback) {
			const userStore = useUserStore();
			const ordersStore = useOrdersStore();

			API.init().setOrdersFiscalSent(userStore.currBusinessId, orderIds, retrying, (r) => {
				if(r.result && r.result === "OK") {
					ordersStore.ordersUngrouped.forEach((order) => {
						orderIds.forEach((orderId) => {
							if(order.order_id === orderId) {
								order.fiscal_request = true;
							}
						});
						order.payments.forEach((payment) => {
							orderIds.forEach((orderId) => {
								if("p"+payment.payment_id === orderId) {
									payment.fiscal_request = true;
								}
							});
						});
					});
					if(r?.already_handled) {
						callback(true, true);
						return
					}
					callback(true, false);
				} else {
					callback(false, false);
				}
			});
		},
		setReceiptRefunded(receiptId, type, callback) {
			const userStore = useUserStore();
			API.init().setReceiptRefunded(userStore.currBusinessId, receiptId, type, (r) => {
				if(r.result && r.result === "OK") {
					callback(true);
				} else {
					callback(false);
				}
			});
		},
		checkFiscalLastDay(callback) {
			const userStore = useUserStore();
			API.init().getUnfiscalizedOrdersLastDay(userStore.currBusinessId, (r) => {
				if(r.result && r.result === "OK") {
					callback(r);
				} else {
					if(r.details && r.details === "open_orders") {
						const notificationStore = useNotificationStore();
						notificationStore.showNotification("fiscal-close-all-orders");
						callback(null);
					} else {
						callback(false);
					}
				}
			});
		},
		printNewOrders(orders) {

			const epsonStore = useEpsonStore();
			if(epsonStore.hasFiscalPrinter && orders.length > 0) {
				let ordersFiscal = orders.filter((o) => orderFiscalizedStatus(o) !== "fiscalized");
				if(ordersFiscal.length > 0) {
					this.printFiscalReceipt(ordersFiscal);
				}
			}

		},
		parseVatNature(country, vatNature) {
			let res = {
				nature: null,
				regRefIndex: null,
				reg: null,
			};

			if(this.countryVatsNature.hasOwnProperty(country) && typeof vatNature === 'string') {
				let natures = this.countryVatsNature[country];
				let exploded = vatNature.split(".");

				let regRefIndex = parseInt(exploded[exploded.length-1]);
				let nature = exploded.slice(0, exploded.length-1).join(".");

				if(natures.hasOwnProperty(nature)) {
					res.nature = nature;
				}
				if(regRefIndex >= 0 && regRefIndex < natures[nature].length) {
					res.regRefIndex = regRefIndex;
					res.reg = natures[nature][regRefIndex];
				}
			}

			return res;
		},
		getFilteredEpsonZReports(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredEpsonZReports(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessEpsonZReports = [];
				this.businessEpsonZReportsTotal = 0;
				if(r && r.result === "OK") {
					if(r.reports && r.reports.selected && r.reports.selected.length) {
						r.reports.selected.forEach(zReport => {
							this.businessEpsonZReports.push(this.normalizeEpsonZReport(zReport));
						});
						this.businessEpsonZReportsTotal = Number(r.reports.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		normalizeEpsonZReport(report) {
			report.date = report.date ? getLocalDateFromServer(report.date) : null;
			report.z_rep = (""+Number(report.z_rep || 0)).padStart(4, "0");
			report.total_net = Number(report.total_net || 0);
			report.total_refund = Number(report.total_refund || 0);
			report.total_cancel = Number(report.total_cancel || 0);
			report.total_vat = Number(report.total_vat || 0);
			report.total_gross = report.total_vat + report.total_net;
			report.total_ticket = Number(report.total_ticket || 0);
			report.count_commercial_doc = Number(report.count_commercial_doc || 0);
			report.cash_amount = Number(report.cash_amount || 0);
			report.electronic_amount = Number(report.electronic_amount || 0);
			report.ticket_amount = Number(report.ticket_amount || 0);
			report.txt_file_s3_url = report.txt_file_s3_url ?? "";
			return report;
		},
		zReportResultIngest(xmlDataString, result, zRep, serialNumber, baseUrl, xmlFile, txtFile, txtDataString) {
			const body = {
				result,
				zRep,
				serialNumber,
				baseUrl,
				xmlFile,
				txtFile,
				txtDataString,
				totalNet: 0,
				totalRefund: 0,
				totalCancel: 0,
				totalVat: 0,
				breakdown: [],
				countCommercialDoc: 0,
				cashAmount: 0,
				electronicAmount: 0,
				totalTicket: 0,
				ticketAmount: 0
			};

			if(result === "success") {
				const parser = new DOMParser();
				const xmlDoc = parser.parseFromString(xmlDataString, "text/xml");

				const datiRT = xmlDoc.querySelectorAll("Riepilogo");

				datiRT.forEach((element) => {

					let nature = element.querySelector("Natura");
					let vatPercentage;
					let vatValue = 0;
					let netValue;

					if (nature) {
						vatPercentage = 0;
						nature = nature.textContent;
						netValue = Math.round(parseFloat(element.querySelector("ImportoParziale").textContent) * 100);
					} else {
						vatPercentage = Math.round(parseFloat(element.querySelector("AliquotaIVA").textContent) * 100);
						vatValue = Math.round(parseFloat(element.querySelector("Imposta").textContent) * 100);
						netValue = Math.round(parseFloat(element.querySelector("ImportoParziale").textContent) * 100);
					}

					let refundElement = element.querySelector("TotaleAmmontareResi");
					let amountRefund = 0;
					if (refundElement) {
						amountRefund = Math.round(parseFloat(refundElement.textContent) * 100);
					}
					let cancelElement = element.querySelector("TotaleAmmontareAnnulli");
					let amountCancel = 0;
					if (cancelElement) {
						amountCancel = Math.round(parseFloat(cancelElement.textContent) * 100);
					}

					if(netValue !== 0 || vatValue !== 0 || amountRefund !== 0 || amountCancel !== 0) {
						body.totalNet += netValue;
						body.totalVat += vatValue;
						body.totalRefund += amountRefund;
						body.totalCancel += amountCancel;

						body.breakdown.push({vatPercentage, nature, vatValue, netValue, amountRefund, amountCancel});
					}

				})

				let totalsElement = xmlDoc.querySelector("Totali");

				if (totalsElement) {

					let totalCommercialDocElement = totalsElement.querySelectorAll("NumeroDocCommerciali");
					if (totalCommercialDocElement.length) {
						body.countCommercialDoc = parseInt(totalCommercialDocElement[0].textContent);
					}
					let cashAmountElement = totalsElement.querySelector("PagatoContanti")
					if (cashAmountElement) {
						body.cashAmount = Math.round(parseFloat(cashAmountElement.textContent) * 100);
					}
					let electronicAmountElement = totalsElement.querySelector("PagatoElettronico")
					if (electronicAmountElement) {
						body.electronicAmount = Math.round(parseFloat(electronicAmountElement.textContent) * 100);
					}
					let totalTicketElement = totalsElement.querySelector("NumeroTicket");
					if (totalTicketElement) {
						body.totalTicket = parseInt(totalTicketElement.textContent);
					}
					let ticketAmountElement = totalsElement.querySelector("PagatoTicket");
					if (ticketAmountElement) {
						body.ticketAmount = Math.round(parseFloat(ticketAmountElement.textContent) * 100);
					}

				}
			}

			API.init().zReportResultIngest(useUserStore().currBusinessId, body, (response) => {
				let notificationStore = useNotificationStore();

				if(this.zreportToReinjest && response.hasOwnProperty('result') && response.result === "OK" && result === "success") {	
					this.zreportToReinjest.retrySucceded = true;
				}
				else if(this.zreportToReinjest) {
					notificationStore.showNotification('generic-error');
					this.zreportToReinjest = null;
				}
			});
		}
	}
});